/**
 * Budget — /budget (vraag Joris 27 sep 2026).
 *
 * Per account: realisatie van de afgelopen jaren (Gripp-facturen), forecast lopend jaar,
 * target volgend jaar, al getekend, gewogen pijplijn en het gat. Klik op een account voor
 * het detailpaneel; export in de kolomindeling van het Revenue-blad van de reforecast-Excel.
 * Rekenregels: ai-engine/services/budget.py · plan: docs/PLAN-BUDGET-2027.md.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { useUserRole } from '../../contexts/UserRoleContext';
import { budgetApi } from './budgetApi';
import { MAANDNAMEN, euro, gatTekst, groepeer } from './budgetLogica';
import BudgetPaneel from './BudgetPaneel';
import GewichtenDialoog from './GewichtenDialoog';
import ImportDialoog from './ImportDialoog';
import TargetCel from './TargetCel';

const KOP = { fontSize: 12, fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' };
const NUM = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

function Totaal({ label, waarde }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: '#64748b' }}>{label}</Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1f2937', ...NUM }}>{waarde || '—'}</Typography>
    </Box>
  );
}

export default function Budget() {
  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(true);
  const [zoek, setZoek] = useState('');
  const [open, setOpen] = useState(null);
  const [ingeklapt, setIngeklapt] = useState({ zonder: true, leeg: true });
  const [gewichtenOpen, setGewichtenOpen] = useState(false);
  const [exporteren, setExporteren] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { permissions } = useUserRole() || {};
  const magWijzigen = !!permissions?.budget?.edit;
  const magImporteren = magWijzigen && !!permissions?.admin_settings?.edit;

  const laad = useCallback(async () => {
    setLaden(true);
    try { setData(await budgetApi.overzicht()); } catch (e) { toast.error(`Budget laden mislukt: ${e.message}`); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  const jaar = data?.jaar;
  const vorig = data?.vorig_jaar;
  const groepen = useMemo(() => (data ? groepeer(data.regels, zoek, data.vorig_jaar) : []), [data, zoek]);

  const exporteer = async () => {
    setExporteren(true);
    try {
      const blob = await budgetApi.export(jaar);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Budget_${jaar}_revenue.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); } finally { setExporteren(false); }
  };

  const tot = data?.totaal;
  const stand = data ? (data.gerealiseerde_maanden > 0
    ? `Realisatie ${vorig} t/m ${MAANDNAMEN[data.gerealiseerde_maanden - 1]}` : `Nog geen realisatie ${vorig}`) : '';

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader icon="savings" title={jaar ? `Budget ${jaar}` : 'Budget'}
        subtitle="Omzet per account, het target voor volgend jaar en hoe we daar komen"
        rightActions={data && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {magImporteren && (
              <Button size="small" onClick={() => setImportOpen(true)} sx={{ textTransform: 'none' }}>Targets {vorig} uit Excel</Button>
            )}
            <Button size="small" onClick={() => setGewichtenOpen(true)} sx={{ textTransform: 'none' }}>Maandverdeling</Button>
            <Button size="small" variant="contained" disableElevation onClick={exporteer} disabled={exporteren}
              sx={{ textTransform: 'none', fontWeight: 700 }}>{exporteren ? 'Bezig…' : 'Exporteer naar Excel'}</Button>
          </Box>
        )} />

      {laden && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : data && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            <Totaal label={`Realisatie ${jaar - 3}`} waarde={euro(tot.realisatie[String(jaar - 3)])} />
            <Totaal label={`Realisatie ${jaar - 2}`} waarde={euro(tot.realisatie[String(jaar - 2)])} />
            <Totaal label={`Forecast ${vorig}`} waarde={euro(tot.forecast_vorig)} />
            <Totaal label={`Target ${jaar}`} waarde={euro(tot.target)} />
            <Totaal label="Gat na getekend en pijplijn" waarde={euro(tot.gat)} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Zoek account of bedrijf" value={zoek} onChange={(e) => setZoek(e.target.value)}
              sx={{ width: 300, maxWidth: '100%' }} />
            <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>
              {stand} · bron: Gripp-facturen · lopend werk = opdrachtwaarde van projecten in uitvoering (deels al gefactureerd) · pijplijn = open offertes van de laatste 12 maanden, gewogen = × kans
              {magWijzigen ? ' · Target: typ en druk Enter' : ''}
            </Typography>
          </Box>

          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={KOP}>Account</TableCell>
                    {[`${jaar - 3}`, `${jaar - 2}`, `Forecast ${vorig}`, `Target ${jaar}`, 'Getekend', 'Lopend werk', 'Pijplijn (open)', 'Pijplijn (gewogen)', 'Gat'].map((h) => (
                      <TableCell key={h} align="right" sx={KOP}>{h}</TableCell>
                    ))}
                    <TableCell sx={KOP}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groepen.map((g) => (
                    <React.Fragment key={g.soort}>
                      <TableRow onClick={() => setIngeklapt((x) => ({ ...x, [g.soort]: !x[g.soort] }))} sx={{ cursor: 'pointer', bgcolor: '#fafbfc' }}>
                        <TableCell colSpan={11} sx={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.04em' }}>
                          {ingeklapt[g.soort] && !zoek ? '▸' : '▾'} {g.label} ({g.regels.length})
                        </TableCell>
                      </TableRow>
                      {(!ingeklapt[g.soort] || zoek) && g.regels.map((r) => {
                        const gat = gatTekst(r.gat);
                        return (
                          <TableRow key={r.sleutel} hover onClick={() => setOpen(r.sleutel)} sx={{ cursor: 'pointer' }}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {r.naam}
                              {r.aantal_bedrijven > 1 && <Box component="span" sx={{ color: '#94a3b8', fontWeight: 400 }}> · {r.aantal_bedrijven} bedrijven</Box>}
                            </TableCell>
                            <TableCell align="right" sx={NUM}>{euro(r.realisatie[String(jaar - 3)])}</TableCell>
                            <TableCell align="right" sx={NUM}>{euro(r.realisatie[String(jaar - 2)])}</TableCell>
                            <TableCell align="right" sx={NUM}>{euro(r.forecast_vorig)}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, fontWeight: 700, py: 0.25 }}>
                              <TargetCel regel={r} jaar={jaar} magWijzigen={magWijzigen} onOpgeslagen={laad} />
                            </TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: '#64748b' }}>{euro(r.getekend)}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: '#64748b' }}>{r.lopend_waarde ? euro(r.lopend_waarde) : ''}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: '#64748b' }}>{r.pijplijn_open ? euro(r.pijplijn_open) : ''}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: '#64748b' }}>{r.pijplijn_gewogen ? euro(r.pijplijn_gewogen) : ''}</TableCell>
                            <TableCell align="right" sx={{ ...NUM, color: gat.open ? '#b42318' : '#64748b' }}>{gat.tekst}</TableCell>
                            <TableCell sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>{r.status}</TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  {groepen.length === 0 && (
                    <TableRow><TableCell colSpan={11} sx={{ color: '#94a3b8', py: 4, textAlign: 'center' }}>
                      {zoek ? 'Niets gevonden.' : 'Nog geen omzet of accounts gevonden.'}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {data.conflicten?.length > 0 && (
            <Box component="details" sx={{ mt: 2, color: '#64748b', fontSize: 13 }}>
              <summary style={{ cursor: 'pointer' }}>Koppelingen nakijken: {data.conflicten.length} {data.conflicten.length === 1 ? 'bedrijf staat' : 'bedrijven staan'} bij meer dan één account</summary>
              {data.conflicten.map((c) => (
                <Typography key={c.company_id} sx={{ fontSize: 13, mt: 0.5 }}>
                  {c.bedrijf}: telt bij {c.accounts[0]} (ook gekoppeld aan {c.accounts.slice(1).join(', ')})
                </Typography>
              ))}
            </Box>
          )}
        </>
      )}

      {data && (
        <Box component="details" sx={{ mt: 2, color: '#64748b', fontSize: 13 }}>
          <summary style={{ cursor: 'pointer' }}>Verschillen met de reforecast-Excel</summary>
          <Typography sx={{ fontSize: 13, mt: 0.5 }}>
            De tool rekent, de Excel is alleen voor aanlevering. Bewust anders dan het Revenue-blad:
          </Typography>
          {[
            `Realisatie ${vorig} telt t/m vorige maand (Excel: vast jan–jun).`,
            `De rest van ${vorig} wordt helemaal verdeeld; in de Excel tellen de gewichten jul–dec op tot 0,84, dus 16% van "wat nog te halen" valt daar weg.`,
            `${jaar} heeft 12 maanden; in de Excel alleen jan–jun, en jan/feb verwijzen voor de meeste rijen naar lege cellen (= 0).`,
            'De statusformule (Upsell/Downsell/…) staat hier op elke regel; in de Excel ontbreekt die vanaf rij 100.',
            'Omzet komt uit de Gripp-facturen; de OHW-regel uit de Excel (niet gefactureerd) staat hier als Excel-groep met alleen een target.',
          ].map((t) => <Typography key={t} sx={{ fontSize: 13, mt: 0.5 }}>· {t}</Typography>)}
        </Box>
      )}

      {open && (
        <BudgetPaneel sleutel={open} jaar={jaar} keuzes={data?.keuzes} magWijzigen={magWijzigen}
          onClose={() => setOpen(null)} onGewijzigd={laad} />
      )}
      {data && magImporteren && (
        <ImportDialoog open={importOpen} jaar={jaar} onClose={() => setImportOpen(false)} onGeimporteerd={laad} />
      )}
      {data && (
        <GewichtenDialoog open={gewichtenOpen} jaar={jaar} gewichten={data.gewichten} bron={data.gewichten_bron}
          versie={data.gewichten_versie} magWijzigen={magWijzigen} onClose={() => setGewichtenOpen(false)} onOpgeslagen={laad} />
      )}
    </Box>
  );
}
